/**
 * DeleteSnapshotConfirmationModal Component
 *
 * Simple confirmation modal for deleting a market snapshot entry.
 * Displays the instrument/timeframe being deleted and confirms the action.
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from '@enfomentis/ui';

// 3. Types
import type { MarketStateDraft } from '@/types/transient/MarketStateDraft';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for DeleteSnapshotConfirmationModal
 */
export interface DeleteSnapshotConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The snapshot draft being deleted */
  snapshot: MarketStateDraft | null;
  /** Callback when modal closes (cancel) */
  onClose: () => void;
  /** Callback when delete is confirmed */
  onConfirm: () => void;
}

// =============================================================================
// Component
// =============================================================================

/**
 * DeleteSnapshotConfirmationModal Component
 */
export const DeleteSnapshotConfirmationModal: React.FC<DeleteSnapshotConfirmationModalProps> = ({
  isOpen,
  snapshot,
  onClose,
  onConfirm,
}) => {
  // Derive display name
  const instrumentName =
    snapshot?.instrumentSnapshot?.displayName ??
    snapshot?.instrumentSnapshot?.symbol ??
    'Unknown instrument';
  const timeframe = snapshot?.timeframe ?? '';
  const displayName = timeframe ? `${instrumentName} / ${timeframe}` : instrumentName;

  // Check if snapshot has any data entered
  const hasData = snapshot?.snapshots && snapshot.snapshots.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      placement="center"
    >
      <ModalContent>
        <ModalHeader>Delete &apos;{displayName}&apos;?</ModalHeader>

        <ModalBody>
          <p className="text-sm text-text-secondary">
            {hasData
              ? 'This snapshot has data entered. Deleting it will remove all associated data.'
              : 'This will remove the snapshot from your pre-trading analysis.'}
          </p>
          <p className="text-sm text-text-secondary mt-2">
            This action cannot be undone.
          </p>
        </ModalBody>

        <ModalFooter>
          <Button variant="light" onPress={onClose}>
            Cancel
          </Button>
          <Button
            color="danger"
            onPress={() => {
              onConfirm();
              onClose();
            }}
          >
            Delete
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteSnapshotConfirmationModal;
